// ═══════════════════════════════════════════════════════════════
// /frontend/src/pages/DiscussionPage.jsx
// PICH AI — Discussion
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  CpuChipIcon,
  ChatBubbleOvalLeftIcon,
  MapPinIcon,
  LinkIcon,
  PhotoIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  FlagIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ScaleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { eventsAPI, discussionsAPI } from '../services/api';

// ═══════════════════════════════════════════════════════════════
const C = {
  bg: '#F9FAFB',           
  surface: '#FFFFFF',
  border: '#E5E7EB',
  text: '#111827',
  textMuted: '#6B7280',
  accent: '#1F2937',
  accentSoft: '#F3F4F6',
  green: '#059669',
  greenSoft: '#ECFDF5',
  red: '#DC2626',
  redSoft: '#FEF2F2',
  amber: '#D97706',
  amberSoft: '#FFFBEB',
  purple: '#6D28D9',
  purpleSoft: '#F5F3FF',
  credibleColor: '#2563EB',      
  notCredibleColor: '#111827',    
  proofBg: '#F0F9FF',             
  proofBorder: '#BAE6FD',
  aiBg: '#F8FAFC',
};


const Shimmer = ({ w = '100%', h = 16, r = 8 }) => (
  <div style={{
    width: w, height: h, borderRadius: r,
    background: `linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite ease-in-out',
  }} />
);

const SkeletonComment = () => (
  <div style={{ display: 'flex', gap: 12, padding: '24px 0' }}>
    <Shimmer w={40} h={40} r="50%" />
    <div style={{ flex: 1 }}>
      <Shimmer w={80} h={14} style={{ marginBottom: 8 }} />
      <Shimmer w="90%" h={14} style={{ marginBottom: 6 }} />
      <Shimmer w="70%" h={14} />
    </div>
  </div>
);


const badgeStyle = (type) => {
  switch (type) {
    case 'opinion':    return { bg: C.greenSoft, color: C.green, icon: <ChatBubbleOvalLeftIcon width={12} />, label: 'Avis' };
    case 'proof':      return { bg: C.proofBg, color: '#0369A1', icon: <ShieldCheckIcon width={12} />, label: 'Preuve' };
    case 'question':   return { bg: C.purpleSoft, color: C.purple, icon: <QuestionMarkCircleIcon width={12} />, label: 'Question' };
    case 'correction': return { bg: C.redSoft, color: C.red, icon: <FlagIcon width={12} />, label: 'Correction' };
    case 'ai_answer':  return { bg: C.accentSoft, color: C.accent, icon: <CpuChipIcon width={12} />, label: 'IA' };
    default:           return { bg: C.accentSoft, color: C.textMuted, icon: null, label: '' };
  }
};


const ContributionCard = ({ comment, onHelpful, onNotHelpful, onReport }) => {
  const { t } = useTranslation();
  const badge = badgeStyle(comment.comment_type);
  const [voted, setVoted] = useState(false);

  const isAi = comment.comment_type === 'ai_answer' || comment.is_ai_generated === true;
  const isProof = comment.comment_type === 'proof';

  const trigger = (action) => {
    if (!voted) {
      setVoted(true);
      action?.(comment.id);
    }
  };

  const formattedDate = comment.created_at
    ? new Date(comment.created_at).toLocaleString([], {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div style={{
      padding: '20px',
      marginBottom: 8,
      background: isProof ? C.proofBg : isAi ? C.aiBg : C.surface,
      borderRadius: 12,
      border: isProof ? `1px solid ${C.proofBorder}` : '1px solid transparent',
      transition: 'background 0.2s',
    }}>
      <div style={{ display: 'flex', gap: 12 }}>
      
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: isAi ? '#E5E7EB' : C.border,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: C.text, flexShrink: 0,
        }}>
          {isAi ? <CpuChipIcon width={20} /> : (comment.user_display || comment.user || '?')[0].toUpperCase()}
        </div>

        <div style={{ flex: 1 }}>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              {isAi ? 'PichAI' : (comment.user_display || comment.user || 'Anonyme')}
            </span>
            {comment.department && (
              <span style={{ fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPinIcon width={12} /> {comment.department}
              </span>
            )}
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
              background: badge.bg, color: badge.color,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {badge.icon} {badge.label}
            </span>
            <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 'auto' }}>
              {formattedDate}
            </span>
          </div>

          
          <p style={{ fontSize: 15, color: C.text, margin: '4px 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {comment.content}
          </p>

         
          {isProof && comment.media_urls?.length > 0 && (
            <div style={{ marginTop: 12, background: '#FFFFFF', borderRadius: 8, border: '1px solid #E5E7EB', padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <LinkIcon width={14} style={{ color: '#0369A1' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#0369A1' }}>Source</span>
              </div>
              {comment.media_urls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.text, wordBreak: 'break-all' }}>
                  {url}
                </a>
              ))}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <ScaleIcon width={14} style={{ color: C.textMuted }} />
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  Fiabilité estimée : {Math.min(95, (comment.helpful_count || 0) * 10 + 60)}%
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 12 }}>
            <button onClick={() => trigger(onHelpful)} disabled={voted}
              style={{ background: 'none', border: 'none', color: voted ? C.green : C.textMuted, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <HandThumbUpIcon width={16} /> {t('discussion.helpful')} ({comment.helpful_count ?? 0})
            </button>
            <button onClick={() => trigger(onNotHelpful)} disabled={voted}
              style={{ background: 'none', border: 'none', color: voted ? C.red : C.textMuted, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <HandThumbDownIcon width={16} /> {t('discussion.not_helpful')} ({comment.not_helpful_count ?? 0})
            </button>
            <button onClick={() => onReport?.(comment.id)}
              style={{ background: 'none', border: 'none', color: C.textMuted, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FlagIcon width={16} /> {t('discussion.report')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const AskAIModal = ({ claimId, isOpen, onClose, onNewComment, questionsRemaining }) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  if (!isOpen) return null;
  const canAsk = questionsRemaining === undefined || questionsRemaining > 0;

  const submit = async () => {
    if (!question.trim() || loading || !canAsk) return;
    setLoading(true);
    try {
      const { data: qData } = await discussionsAPI.postComment(claimId, { content: question, comment_type: 'question' });
      if (qData.comment) onNewComment?.(qData.comment);
      const { data: aiData } = await discussionsAPI.askAI(claimId, question);
      const answerText = aiData.answer || t('discussion.ask_ai_no_response');
      const { data: aData } = await discussionsAPI.postComment(claimId, { content: answerText, comment_type: 'ai_answer' });
      if (aData.comment) onNewComment?.({ ...aData.comment, is_ai_generated: true, comment_type: 'ai_answer' });
    } catch (err) {
      console.error(err);
      alert(t('discussion.ask_ai_error'));
    } finally {
      setLoading(false);
      setQuestion('');
      onClose();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 16, padding: 24, maxWidth: 500, width: '90%', boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
            <SparklesIcon width={18} /> {t('discussion.ask_ai_title')}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none' }}><XMarkIcon width={20} /></button>
        </div>
        {!canAsk && <p style={{ color: C.red, fontSize: 14, marginBottom: 16 }}>{t('discussion.question_limit_reached')}</p>}
        <textarea rows={3} placeholder={t('discussion.ask_ai_placeholder')} value={question} onChange={e => setQuestion(e.target.value)}
          style={{ width: '100%', padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.bg, resize: 'vertical', marginBottom: 16, opacity: canAsk ? 1 : 0.5 }} disabled={!canAsk} />
        <button onClick={submit} disabled={!question.trim() || loading || !canAsk}
          style={{ width: '100%', padding: 12, borderRadius: 30, background: C.accent, color: '#fff', fontWeight: 600, border: 'none', cursor: 'pointer', opacity: question.trim() && !loading && canAsk ? 1 : 0.6 }}>
          {loading ? '...' : t('discussion.ask')}
        </button>
      </div>
    </div>
  );
};


const AIInsightSidebar = ({ claimId, onAskAI, analysisTrigger, questionsRemaining }) => {
  const { t } = useTranslation();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!claimId) return;
      setLoading(true);
      try {
        const { data } = await discussionsAPI.getAnalysis(claimId);
        if (!cancelled) setAnalysis(data);
      } catch (e) { console.warn(e); }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [claimId, analysisTrigger]);

  return (
    <div style={{ position: 'sticky', top: 80, alignSelf: 'start', width: 320 }}>
     
      <div style={{ background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <CpuChipIcon width={18} style={{ color: C.accent }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{t('discussion.ai_insight')}</span>
        </div>
        {loading ? <Shimmer h={60} /> : (
          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 }}>
            {analysis?.analysis?.summary || 'Analyse en cours...'}
          </p>
        )}
      </div>

      <button onClick={onAskAI} style={{
        width: '100%', padding: '12px', borderRadius: 40,
        border: 'none', background: C.accent, color: '#fff',
        fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer'
      }}>
        <SparklesIcon width={16} /> {t('discussion.ask_ai')}
      </button>
      {typeof questionsRemaining === 'number' && (
        <p style={{ fontSize: 11, color: C.textMuted, marginTop: 8, textAlign: 'center' }}>
          {questionsRemaining > 0 ? t('discussion.questions_remaining', { count: questionsRemaining }) : t('discussion.question_limit_reached')}
        </p>
      )}
    </div>
  );
};

const ConsensusBar = ({ event, userVote, onVote, t }) => {
  if (!event) return null;
  const total = event.participants || 0;
  if (total === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
        <UserGroupIcon width={18} style={{ color: C.textMuted }} />
        <span style={{ fontSize: 14, color: C.textMuted }}>{t('discussion.no_votes_yet')}</span>
      </div>
    );
  }
  const credible = event.currentConsensus ?? 0;
  const notCredible = 100 - credible;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: C.credibleColor }}>{credible}%</span>
          <span style={{ display: 'block', fontSize: 10, color: C.textMuted }}>{t('event.credible')}</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: C.notCredibleColor }}>{notCredible}%</span>
          <span style={{ display: 'block', fontSize: 10, color: C.textMuted }}>{t('event.not_credible')}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => onVote('probable')} style={{
            padding: '8px 16px', borderRadius: 30,
            border: userVote?.value === 'probable' ? `2px solid ${C.credibleColor}` : `1px solid ${C.border}`,
            background: userVote?.value === 'probable' ? `${C.credibleColor}10` : 'transparent',
            color: userVote?.value === 'probable' ? C.credibleColor : C.text,
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}><CheckCircleIcon width={14} /> {t('event.credible')}</button>
          <button onClick={() => onVote('improbable')} style={{
            padding: '8px 16px', borderRadius: 30,
            border: userVote?.value === 'improbable' ? `2px solid ${C.notCredibleColor}` : `1px solid ${C.border}`,
            background: userVote?.value === 'improbable' ? `${C.notCredibleColor}10` : 'transparent',
            color: userVote?.value === 'improbable' ? C.notCredibleColor : C.text,
            fontWeight: 600, fontSize: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}><XCircleIcon width={14} /> {t('event.not_credible')}</button>
        </div>
      </div>
      <span style={{ fontSize: 12, color: C.textMuted }}>{total.toLocaleString()} {t('event.participants')}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
const CommentFeed = ({ comments, loading, activeTab, tabs, onTabChange, onHelpful, onNotHelpful, onReport, t }) => {
  const tabsRef = useRef(null);
  const filtered = comments.filter(c => activeTab === 'all' || c.comment_type === activeTab);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Onglets */}
      <div ref={tabsRef} className="tabs-scroll" style={{
        display: 'flex', gap: 8, borderBottom: `1px solid ${C.border}`,
        marginBottom: 24, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4,
      }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => onTabChange(tab)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none',
              fontSize: 13, fontWeight: activeTab === tab ? 700 : 500,
              color: activeTab === tab ? C.accent : C.textMuted,
              borderBottom: activeTab === tab ? `2px solid ${C.accent}` : '2px solid transparent',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
            {t(`discussion.tab_${tab}`)}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <><SkeletonComment /><SkeletonComment /><SkeletonComment /></>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>{t('discussion.no_comments')}</div>
      ) : (
        filtered.map(comment => (
          <ContributionCard key={comment.id} comment={comment}
            onHelpful={onHelpful} onNotHelpful={onNotHelpful} onReport={onReport} />
        ))
      )}
    </div>
  );
};


const CommentComposer = ({ commentInput, setCommentInput, selectedType, setSelectedType,
  selectedDepartment, setSelectedDepartment, onPublish, imageFile, setImageFile,
  imagePreview, setImagePreview, linkUrl, setLinkUrl, linkInputVisible, setLinkInputVisible, t }) => {
  const fileInputRef = useRef(null);
  const departments = ['Artibonite', 'Centre', "Grand'Anse", 'Nippes', 'Nord', 'Nord-Est', 'Nord-Ouest', 'Ouest', 'Sud', 'Sud-Est'];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 15, background: C.surface, borderTop: `1px solid ${C.border}`, padding: '16px 24px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {imagePreview && (
          <div style={{ position: 'relative', width: 60, height: 60, borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
            <img src={imagePreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button onClick={() => { setImageFile(null); setImagePreview(null); }}
              style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: 20, height: 20, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <XMarkIcon width={12} />
            </button>
          </div>
        )}
        {linkInputVisible && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="url" placeholder="https://..." value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 20, border: `1px solid ${C.border}`, fontSize: 13 }} />
            <button onClick={() => { setLinkInputVisible(false); setLinkUrl(''); }}
              style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer' }}><XMarkIcon width={18} /></button>
          </div>
        )}

        <textarea value={commentInput} onChange={e => setCommentInput(e.target.value)}
          placeholder={t('discussion.input_placeholder')} rows={2}
          style={{ width: '100%', padding: 12, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, resize: 'vertical' }} />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 20, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13 }}>
            <option value="opinion">💬 {t('discussion.type_opinion')}</option>
            <option value="proof">🔗 {t('discussion.type_proof')}</option>
            <option value="question">❓ {t('discussion.type_question')}</option>
            <option value="correction">⚠️ {t('discussion.type_correction')}</option>
          </select>

          <select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 20, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13 }}>
            <option value="">{t('discussion.select_department')}</option>
            {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
          </select>

          <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageSelect} />
          <button onClick={() => fileInputRef.current?.click()}
            style={{ padding: '8px 12px', borderRadius: 20, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <PhotoIcon width={16} /> {t('discussion.upload')}
          </button>
          <button onClick={() => setLinkInputVisible(!linkInputVisible)}
            style={{ padding: '8px 12px', borderRadius: 20, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <LinkIcon width={16} /> {t('discussion.add_link')}
          </button>
          <button onClick={onPublish} disabled={!commentInput.trim() && !imageFile && !linkUrl}
            style={{ marginLeft: 'auto', padding: '10px 24px', borderRadius: 30, border: 'none', background: C.accent, color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: (!commentInput.trim() && !imageFile && !linkUrl) ? 0.5 : 1 }}>
            {t('discussion.publish')}
          </button>
        </div>
      </div>
    </div>
  );
};


const DiscussionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const actualId = slug;

  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [userVote, setUserVote] = useState(null);
  const [votesSummary, setVotesSummary] = useState(null);
  const [loading, setLoading] = useState({ event: true, comments: true });
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('all');
  const [commentInput, setCommentInput] = useState('');
  const [selectedType, setSelectedType] = useState('opinion');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [analysisTrigger, setAnalysisTrigger] = useState(0);
  const [questionsRemaining, setQuestionsRemaining] = useState(undefined);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [linkInputVisible, setLinkInputVisible] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!actualId) return;
    setError(null);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading({ event: true, comments: true });
    try {
      const claimRes = await eventsAPI.getEventById(actualId);
      if (signal.aborted) return;
      let raw = claimRes.data;
      if (raw?.event) raw = raw.event;
      else if (raw?.claim) raw = raw.claim;
      const normalized = {
        ...raw,
        title: raw.title || raw.claim?.title || '',
        crowd_confidence_score: raw.crowd_confidence_score ?? 0,
        total_votes: raw.total_votes ?? raw.votes_total ?? 0,
      };
      setEvent({
        ...normalized,
        currentConsensus: normalized.total_votes > 0 ? Math.round(normalized.crowd_confidence_score * 100) : null,
        participants: normalized.total_votes || 0,
      });
      const commRes = await discussionsAPI.getComments(actualId, { sort: 'recent', limit: 50 });
      if (signal.aborted) return;
      const fetched = (commRes.data.comments || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setComments(fetched);
      try { const voteRes = await discussionsAPI.getUserVote(actualId); if (!signal.aborted) setUserVote(voteRes.data); } catch (e) {}
      const summaryRes = await discussionsAPI.getVotesSummary(actualId);
      if (!signal.aborted) {
        setVotesSummary(summaryRes.data);
        if (summaryRes.data?.questions_remaining !== undefined) setQuestionsRemaining(summaryRes.data.questions_remaining);
      }
    } catch (err) {
      if (!signal.aborted) setError(err.message || t('common.error'));
    } finally {
      if (!signal.aborted) setLoading({ event: false, comments: false });
    }
  }, [actualId, t]);

  useEffect(() => { fetchData(); return () => abortRef.current?.abort(); }, [fetchData]);

  const handleVote = async (vote) => {
    if (!actualId) return;
    try {
      const { data } = await eventsAPI.submitClaimVote(actualId, { vote });
      if (data.voted) {
        setUserVote(data);
        const summary = await discussionsAPI.getVotesSummary(actualId);
        setVotesSummary(summary.data);
        if (summary.data?.questions_remaining !== undefined) setQuestionsRemaining(summary.data.questions_remaining);
      }
    } catch (e) {}
  };

  const handlePublish = async () => {
    if (!commentInput.trim() && !imageFile && !linkUrl) return;
    const payload = {
      content: commentInput,
      comment_type: selectedType,
      department: selectedDepartment || undefined,
      media_urls: [],
    };
    if (linkUrl) { payload.content += `\n${linkUrl}`; setLinkUrl(''); setLinkInputVisible(false); }
    if (imageFile) { payload.media_urls.push(URL.createObjectURL(imageFile)); setImageFile(null); setImagePreview(null); }
    try {
      const { data } = await discussionsAPI.postComment(actualId, payload);
      if (data.comment) {
        setComments(prev => [...prev, data.comment].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
        setCommentInput('');
        setSelectedDepartment('');
        if (selectedType === 'proof' || selectedType === 'correction') setAnalysisTrigger(p => p + 1);
      }
    } catch (err) { alert(t('discussion.post_error')); }
  };

  const addNewComment = (newComment) => {
    if (newComment) {
      setComments(prev => [...prev, newComment].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      if (newComment.comment_type === 'proof' || newComment.comment_type === 'correction') setAnalysisTrigger(p => p + 1);
    }
  };

  const handleHelpful = async (id) => {
    try { const { data } = await discussionsAPI.signalComment(id, 'helpful'); setComments(prev => prev.map(c => c.id === id ? { ...c, helpful_count: data.helpful_count } : c)); } catch (e) {}
  };
  const handleNotHelpful = async (id) => {
    try { const { data } = await discussionsAPI.signalComment(id, 'not_helpful'); setComments(prev => prev.map(c => c.id === id ? { ...c, not_helpful_count: data.not_helpful_count } : c)); } catch (e) {}
  };
  const handleReport = async (id) => { try { await discussionsAPI.signalComment(id, 'spam'); alert(t('discussion.report_thanks')); } catch (e) {} };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}>
    
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text }}>
              <ArrowLeftIcon width={20} />
            </button>
            {loading.event ? <Shimmer w="60%" h={24} /> : (
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, flex: 1, color: C.text }}>
                {event?.title || t('event.no_title')}
              </h1>
            )}
          </div>
          {!loading.event && event && <ConsensusBar event={event} userVote={userVote} onVote={handleVote} t={t} />}
        </div>
      </div>

  
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 0', display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        <CommentFeed
          comments={comments}
          loading={loading.comments}
          activeTab={activeTab}
          tabs={['all', 'proof', 'question', 'correction']}
          onTabChange={setActiveTab}
          onHelpful={handleHelpful}
          onNotHelpful={handleNotHelpful}
          onReport={handleReport}
          t={t}
        />

        <div className="desktop-sidebar" style={{ width: 320, flexShrink: 0 }}>
          {!loading.event && <AIInsightSidebar claimId={actualId} onAskAI={() => setAiModalOpen(true)} analysisTrigger={analysisTrigger} questionsRemaining={questionsRemaining} />}
        </div>

        <div className="mobile-fab" style={{ display: 'none', position: 'fixed', bottom: 100, right: 16, zIndex: 20 }}>
          <button onClick={() => setMobileSheetOpen(true)} style={{ width: 48, height: 48, borderRadius: '50%', background: C.accent, border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <CpuChipIcon width={24} />
          </button>
        </div>
      </div>

      <CommentComposer
        commentInput={commentInput} setCommentInput={setCommentInput}
        selectedType={selectedType} setSelectedType={setSelectedType}
        selectedDepartment={selectedDepartment} setSelectedDepartment={setSelectedDepartment}
        onPublish={handlePublish}
        imageFile={imageFile} setImageFile={setImageFile}
        imagePreview={imagePreview} setImagePreview={setImagePreview}
        linkUrl={linkUrl} setLinkUrl={setLinkUrl}
        linkInputVisible={linkInputVisible} setLinkInputVisible={setLinkInputVisible}
        t={t}
      />

      {mobileSheetOpen && (
        <>
          <div onClick={() => setMobileSheetOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.surface, borderRadius: '20px 20px 0 0', padding: 20, zIndex: 1001, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={() => setMobileSheetOpen(false)} style={{ background: 'none', border: 'none' }}><XMarkIcon width={24} /></button>
            </div>
            <AIInsightSidebar claimId={actualId} onAskAI={() => { setAiModalOpen(true); setMobileSheetOpen(false); }} analysisTrigger={analysisTrigger} questionsRemaining={questionsRemaining} />
          </div>
        </>
      )}
      <AskAIModal claimId={actualId} isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} onNewComment={addNewComment} questionsRemaining={questionsRemaining} />

      <style>{`
        .tabs-scroll::-webkit-scrollbar { display: none; }
        @media (max-width: 768px) { .desktop-sidebar { display: none !important; } .mobile-fab { display: block !important; } }
        @media (min-width: 769px) { .mobile-fab { display: none !important; } .desktop-sidebar { display: block !important; } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
};

export default DiscussionPage;